import type { CanvasNote, LifeSpace, Project, Task } from '@/src/renderer/lib/db/models'
import { favoriteTaskPlace, type FavoriteTaskPlace } from '@/src/renderer/lib/favorites'
import { setPendingCanvasJump, setPendingPageJump, setPendingQuickNotesJump, setPendingTaskJump } from './pendingTaskJump'

/** Die Seiten eines Lebensbereichs, auf denen eine Aufgabe ohne Projekt steht, dazu Ziele — die Ziele der „Gehe zu“-Aktionen. */
export type SearchPage = Exclude<FavoriteTaskPlace['kind'], 'project'> | 'goals'

/** Ein Treffer der **Suche** (#233), so wie das Overlay ihn meldet. */
export type SearchHit =
  | { kind: 'task'; task: Task }
  | { kind: 'project'; project: Project }
  | { kind: 'note'; note: CanvasNote; project: Project }
  /** „Gehe zu …“ — eine Seite des aktiven Lebensbereichs. */
  | { kind: 'page'; page: SearchPage; lifeSpace: LifeSpace }

export type SearchTarget =
  | { kind: 'project'; lifeSpace: LifeSpace; projectId: string }
  | { kind: 'page'; lifeSpace: LifeSpace; page: SearchPage }

/** Die Felder eines Tabs, die der Sprung setzt — der Tab-Typ selbst liegt in `hooks/`. */
export interface SearchTab<Icon> {
  id: string
  path: string
  title: string
  icon?: Icon
  lifeSpace: string
  isDirty: boolean
  isPinned: boolean
}

export interface OpenSearchHitDeps<Icon> {
  activeLifeSpace: string
  /** Neuer Tab — oder der schon offene mit demselben Pfad (`useTabManager.openTab`). */
  openTab: (tab: SearchTab<Icon>) => void
  /** Wie ein Klick in der Sidebar (`useTabManager.openPreviewTab`). */
  openPreviewTab: (tab: Pick<SearchTab<Icon>, 'path' | 'title' | 'icon' | 'lifeSpace'>) => void
  /** Die App in einen anderen Lebensbereich wechseln, bevor der Tab dort aufgeht. */
  switchLifeSpace: (lifeSpace: LifeSpace) => void
  getTaskAncestors: (id: string) => Promise<Task[]>
  /** Titel und Icon des Tabs — Namen und Icons kennt die Oberfläche, nicht `lib/`. */
  describe: (target: SearchTarget) => { title: string; icon?: Icon }
}

export function searchTargetPath(target: SearchTarget): string {
  return target.kind === 'project'
    ? `/home/${target.lifeSpace}/project/${target.projectId}`
    : `/home/${target.lifeSpace}/${target.page}`
}

/**
 * Wo eine Aufgabe ohne Projekt steht, sagt ihr Platz; die Seite holt den Sprung ab und markiert sie.
 * Die Details bleiben zu (gewünscht 02.10.2026): Wer sucht, will meist nur hin.
 */
function stashTaskJump(task: Task): SearchTarget {
  const place = favoriteTaskPlace(task)
  if (place.kind === 'project') {
    setPendingTaskJump(place.projectId, task.id)
    return { kind: 'project', lifeSpace: task.lifeSpace, projectId: place.projectId }
  }
  if (place.kind === 'quick-notes') setPendingQuickNotesJump(task.lifeSpace, task.id)
  else setPendingPageJump(place.kind, task.lifeSpace, task.id)
  return { kind: 'page', lifeSpace: task.lifeSpace, page: place.kind }
}

/**
 * **Öffnet einen Treffer der Suche** wie ein Klick in der Sidebar: als Vorschau-Tab, mit
 * `newTab` (⌥↵) als neuer Tab. Ein Treffer aus einem anderen Lebensbereich wechselt die App
 * dorthin und öffnet dort einen Tab.
 *
 * Erst merken, dann öffnen (Muster: Favoriten in der Sidebar): Die Seite holt den Sprung ab,
 * sobald sie steht — oder sofort, wenn sie schon stand. Eine Unteraufgabe öffnet ihre oberste
 * Aufgabe; ein Pinnwand-Treffer öffnet das Projekt mit der Pinnwand.
 */
export async function openSearchHit<Icon>(hit: SearchHit, { newTab }: { newTab: boolean }, deps: OpenSearchHitDeps<Icon>): Promise<void> {
  let target: SearchTarget
  if (hit.kind === 'task') {
    const root = hit.task.parentTaskId ? (await deps.getTaskAncestors(hit.task.id)).at(-1) ?? hit.task : hit.task
    target = stashTaskJump(root)
  } else if (hit.kind === 'project') {
    target = { kind: 'project', lifeSpace: hit.project.lifeSpace, projectId: hit.project.id }
  } else if (hit.kind === 'note') {
    setPendingCanvasJump(hit.project.id, hit.note.id)
    target = { kind: 'project', lifeSpace: hit.project.lifeSpace, projectId: hit.project.id }
  } else {
    target = { kind: 'page', lifeSpace: hit.lifeSpace, page: hit.page }
  }

  const otherSpace = target.lifeSpace !== deps.activeLifeSpace
  if (otherSpace) deps.switchLifeSpace(target.lifeSpace)
  const { title, icon } = deps.describe(target)
  const path = searchTargetPath(target)
  if (newTab || otherSpace) {
    const baseId = target.kind === 'project' ? `${target.lifeSpace}-project-${target.projectId}` : `${target.lifeSpace}-${target.page}`
    deps.openTab({ id: `${baseId}-${Date.now()}`, path, title, icon, lifeSpace: target.lifeSpace, isDirty: false, isPinned: false })
  } else {
    deps.openPreviewTab({ path, title, icon, lifeSpace: target.lifeSpace })
  }
}
